import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Users, 
  Building, 
  Bell, 
  Settings, 
  RefreshCw, 
  ShieldCheck, 
  Flag, 
  UserX, 
  CheckCircle,
  TrendingUp,
  Calendar,
  MessageSquare,
  UserPlus,
  Crown
} from 'lucide-react';
import { ListingSync } from '../components/ListingSync';
import { OpenAITest } from '../components/OpenAITest';
import { FeedbackManagement } from '../components/FeedbackManagement';
import { ManualMatching } from '../components/ManualMatching';
import { RoommatePreferences } from '../components/RoommatePreferences';
import { AdminMonetizationPanel } from '../components/AdminMonetizationPanel';
import { useNavigate } from 'react-router-dom';

interface Profile {
  id: string;
  display_name: string;
  application_text: string;
  moderation_status: string;
  flags: string[];
  created_at: string;
  email: string;
}

interface Stats {
  total_users: number;
  pending_applications: number;
  active_listings: number;
  total_events: number;
  feedback_count: number;
  premium_users: number;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'moderation' | 'listings' | 'feedback' | 'matching' | 'settings' | 'preferences' | 'monetization'>('overview');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [stats, setStats] = useState<Stats>({
    total_users: 0,
    pending_applications: 0,
    active_listings: 0,
    total_events: 0,
    feedback_count: 0,
    premium_users: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'flagged'>('pending');

  useEffect(() => {
    fetchStats();
    if (activeTab === 'moderation') {
      fetchProfiles();
    }
  }, [activeTab, filter]);

  const fetchStats = async () => {
    try {
      const [
        { count: usersCount },
        { count: pendingCount },
        { count: listingsCount },
        { count: eventsCount },
        { count: feedbackCount },
        { count: premiumCount }
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('moderation_status', 'pending'),
        supabase.from('listings').select('*', { count: 'exact', head: true }).eq('is_available', true),
        supabase.from('events').select('*', { count: 'exact', head: true }),
        supabase.from('feedback').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('monetization_enabled', true)
      ]);

      setStats({
        total_users: usersCount || 0,
        pending_applications: pendingCount || 0,
        active_listings: listingsCount || 0,
        total_events: eventsCount || 0,
        feedback_count: feedbackCount || 0,
        premium_users: premiumCount || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchProfiles = async () => {
    try {
      const query = supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query.eq('moderation_status', filter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setProfiles(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const updateModerationStatus = async (profileId: string, status: string, flags?: string[]) => {
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          moderation_status: status,
          flags,
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', profileId);

      if (updateError) throw updateError;
      fetchProfiles();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-500" />
              <h3 className="font-medium">Total Users</h3>
            </div>
            <p className="text-2xl font-bold">{stats.total_users}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-5 h-5 text-yellow-500" />
              <h3 className="font-medium">Pending Applications</h3>
            </div>
            <p className="text-2xl font-bold">{stats.pending_applications}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building className="w-5 h-5 text-green-500" />
              <h3 className="font-medium">Active Listings</h3>
            </div>
            <p className="text-2xl font-bold">{stats.active_listings}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              <h3 className="font-medium">Total Events</h3>
            </div>
            <p className="text-2xl font-bold">{stats.total_events}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-5 h-5 text-pink-500" />
              <h3 className="font-medium">Feedback Items</h3>
            </div>
            <p className="text-2xl font-bold">{stats.feedback_count}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              <h3 className="font-medium">Premium Users</h3>
            </div>
            <p className="text-2xl font-bold">{stats.premium_users}</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-4 border-b mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'overview'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('moderation')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'moderation'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Moderation
          </button>
          <button
            onClick={() => setActiveTab('listings')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'listings'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Listings
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'feedback'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Feedback
          </button>
          <button
            onClick={() => setActiveTab('matching')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'matching'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Matching
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'preferences'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Preferences
          </button>
          <button
            onClick={() => setActiveTab('monetization')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'monetization'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Monetization
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-4 px-4 font-medium whitespace-nowrap ${
              activeTab === 'settings'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-primary-600'
            }`}
          >
            Settings
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">System Status</h2>
              <div className="space-y-4">
                <OpenAITest />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Recent Users</h2>
                  <button
                    onClick={() => navigate('/users')}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    View All
                  </button>
                </div>
                <div className="space-y-2">
                  {profiles.slice(0, 5).map(profile => (
                    <div key={profile.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="font-medium">{profile.display_name || profile.email}</h3>
                        <p className="text-sm text-gray-600">
                          Joined {new Date(profile.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        profile.moderation_status === 'approved' ? 'bg-green-100 text-green-800' :
                        profile.moderation_status === 'rejected' ? 'bg-red-100 text-red-800' :
                        profile.moderation_status === 'flagged' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {profile.moderation_status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Premium Features</h2>
                  <button
                    onClick={() => navigate('/monetization')}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    View All
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5 text-yellow-500" />
                      <span>Premium Users</span>
                    </div>
                    <span className="font-medium">{stats.premium_users}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      <span>Conversion Rate</span>
                    </div>
                    <span className="font-medium">
                      {stats.total_users ? Math.round((stats.premium_users / stats.total_users) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'moderation' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">User Moderation</h2>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as typeof filter)}
                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="all">All Users</option>
                <option value="pending">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="flagged">Flagged</option>
              </select>
            </div>

            {error && (
              <div className="mb-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {profiles.map((profile) => (
                <div key={profile.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {profile.display_name || profile.email}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Joined {new Date(profile.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {profile.moderation_status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateModerationStatus(profile.id, 'approved')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                            title="Approve"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => updateModerationStatus(profile.id, 'rejected')}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                            title="Reject"
                          >
                            <UserX className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => updateModerationStatus(profile.id, 'flagged', ['needs_review'])}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-full"
                            title="Flag for Review"
                          >
                            <Flag className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      <div className={`px-3 py-1 rounded-full text-sm ${
                        profile.moderation_status === 'approved' ? 'bg-green-100 text-green-800' :
                        profile.moderation_status === 'rejected' ? 'bg-red-100 text-red-800' :
                        profile.moderation_status === 'flagged' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {profile.moderation_status.charAt(0).toUpperCase() + profile.moderation_status.slice(1)}
                      </div>
                    </div>
                  </div>

                  <div className="prose prose-sm max-w-none">
                    <h4 className="text-sm font-medium text-gray-700">Application</h4>
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {profile.application_text || 'No application text provided'}
                    </p>
                  </div>

                  {profile.flags && profile.flags.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700">Flags</h4>
                      <div className="flex gap-2 mt-1">
                        {profile.flags.map((flag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                          >
                            {flag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => navigate(`/users/${profile.id}`)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                    >
                      <UserPlus className="w-5 h-5" />
                      <span>View Full Profile</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'listings' && (
          <div className="space-y-6">
            <ListingSync />
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="space-y-6">
            <FeedbackManagement />
          </div>
        )}

        {activeTab === 'matching' && (
          <div className="space-y-6">
            <ManualMatching />
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="space-y-6">
            <RoommatePreferences />
          </div>
        )}

        {activeTab === 'monetization' && (
          <div className="space-y-6">
            <AdminMonetizationPanel />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">System Settings</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-gray-500" />
                    <div>
                      <h3 className="font-medium">Maintenance Mode</h3>
                      <p className="text-sm text-gray-600">Temporarily disable new user registrations</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-gray-500" />
                    <div>
                      <h3 className="font-medium">Email Notifications</h3>
                      <p className="text-sm text-gray-600">Send email alerts for new applications</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-gray-500" />
                    <div>
                      <h3 className="font-medium">Analytics</h3>
                      <p className="text-sm text-gray-600">Collect anonymous usage data</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}